<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';

const props = defineProps({
  customAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update', 'delete']);

const { t } = useI18n();
const getters = useStoreGetters();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();

const isOpen = ref(false);

const attributeDefinitions = computed(() =>
  getters['attributes/getAttributesByModel']
    .value('task_attribute')
    .filter(attr => attr.visible !== false)
);

const attributes = computed(() => {
  const attrs = props.customAttributes || {};
  return attributeDefinitions.value.map(attr => ({
    id: attr.id,
    attributeKey: attr.attribute_key,
    attributeDisplayType: attr.attribute_display_type,
    attributeDisplayName: attr.attribute_display_name,
    attributeDescription: attr.attribute_description,
    attributeValues: attr.attribute_values,
    regexPattern: attr.regex_pattern,
    regexCue: attr.regex_cue,
    value: attr.attribute_key in attrs ? attrs[attr.attribute_key] : '',
  }));
});

const filledCount = computed(
  () =>
    attributes.value.filter(attr => {
      if (attr.attributeDisplayType === 'checkbox') {
        return Boolean(attr.value);
      }
      return (
        attr.value !== null && attr.value !== undefined && attr.value !== ''
      );
    }).length
);

const componentMap = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
  default: OtherAttribute,
};

const attributeComponent = type => componentMap[type] || componentMap.default;

const onUpdate = (key, value) => emit('update', key, value);
const onDelete = key => emit('delete', key);
</script>

<template>
  <section
    class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
  >
    <button
      type="button"
      class="flex items-center w-full gap-2 px-3 py-2.5 text-left rounded-xl hover:bg-n-alpha-1"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <span class="i-lucide-list-checks size-4 shrink-0 text-n-slate-10" />
      <span class="flex-1 min-w-0 text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.MODAL.SECTION_CUSTOM_ATTRIBUTES') }}
      </span>
      <span
        v-if="attributes.length"
        class="text-xs tabular-nums text-n-slate-10"
      >
        {{
          t('KANBAN.CUSTOM_ATTRIBUTES.FILLED', {
            filled: filledCount,
            total: attributes.length,
          })
        }}
      </span>
      <span
        class="size-4 shrink-0 i-lucide-chevron-down text-n-slate-9 transition-transform duration-150"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <div v-if="isOpen" class="flex flex-col gap-3 px-3 pb-3">
      <div v-if="attributes.length" class="flex flex-col gap-3">
        <div
          v-for="attr in attributes"
          :key="attr.id"
          class="group/attribute flex flex-col gap-1.5 min-w-0"
          :class="{
            'flex-row items-center justify-between gap-3':
              attr.attributeDisplayType === 'checkbox',
          }"
        >
          <span
            class="text-xs font-medium leading-4 text-n-slate-11"
            :class="
              attr.attributeDisplayType === 'checkbox'
                ? 'min-w-0 flex-1'
                : 'w-full'
            "
            :title="attr.attributeDescription || attr.attributeDisplayName"
          >
            {{ attr.attributeDisplayName }}
          </span>
          <component
            :is="attributeComponent(attr.attributeDisplayType)"
            class="w-full min-w-0"
            :class="{
              '!w-auto shrink-0': attr.attributeDisplayType === 'checkbox',
            }"
            :attribute="attr"
            is-editing-view
            @update="value => onUpdate(attr.attributeKey, value)"
            @delete="() => onDelete(attr.attributeKey)"
          />
        </div>
      </div>
      <p v-else class="m-0 text-sm leading-5 text-n-slate-11">
        {{ t('KANBAN.CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND') }}
        <router-link
          v-if="isAdmin"
          :to="{
            name: 'attributes_list',
            params: { accountId },
          }"
          class="font-medium text-n-blue-11 hover:text-n-blue-12"
        >
          {{ t('KANBAN.CUSTOM_ATTRIBUTES.CREATE_LINK') }}
        </router-link>
      </p>
    </div>
  </section>
</template>
